import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import advancedTasksApi from "../../../../api/task/advancedTasksApi";
import {
  ScrollableModalContentTemplate,
} from "../../../common";
import {
  SimpleFormInstance,
} from "../../../../types/dtos/simple-forms";
import { UserTaskSaveResponse } from "../../../../types/dtos/tasks/advanced-tasks/api-response/UserTaskSaveResponse";
import { BaseUserDetailsDto } from "../../../../types/dtos/generic";
import InfoAlert from "../../../alerts/InfoAlert";
import WarningAlert from "../../../alerts/WarningAlert";
import ExistingTaskResponseSummaryTable from "../ExistingTaskResponseSummaryTable";
import UserContext from "../../../../state/UserContext";
import { advancedTaskHelper } from "../../../../helpers";
import { TaskEditPopupUsageScenario } from "../../../../types/tasks/TaskEditPopupUsageScenario";
import { AnswerSetUserTaskDetails } from "../../../../types/dtos/tasks/advanced-tasks/add-popup/AnswerSetUserTaskDetails";

interface AdvancedTaskExistingCancelResponseViewProps {
  usageScenario: TaskEditPopupUsageScenario;
  userTaskId: string;
  taskTypeId: string;
  cancelFormInstance: SimpleFormInstance;
  ownerEmployee: BaseUserDetailsDto;
  goBackToBodyDetails(): void;
  onEditResponse(): void;
  onCancelled(): void;

  /* Specific properties only used within journey/collab doc usage **/
  currentAnswerSetUniqueId?: string | null;
  formId?: number | null;
}

const AdvancedTaskExistingCancelResponseView = ({
  usageScenario,
  userTaskId,
  taskTypeId,
  cancelFormInstance,
  ownerEmployee,
  currentAnswerSetUniqueId = null,
  formId = null,
  goBackToBodyDetails,
  onEditResponse,
  onCancelled,
}: AdvancedTaskExistingCancelResponseViewProps) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const auth = useAuth();
  const tasksApi = new advancedTasksApi(auth.user?.access_token);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const matchingTaskType = userContext.user.client.taskTypes.find(
    (tt) => tt.id === taskTypeId
  );

  const singularTaskTypeName = matchingTaskType != undefined ? advancedTaskHelper.ToLowerCase(
    matchingTaskType.singularNameTranslationKeyIdentifier
  ) : "task";

  const onCancelWithForm = () => {
    // There's a form to fill out, so validate then post the response
    if (!userTaskId) return;

    const onCancelError = (error: any) => {
      setIsSaving(false);
      console.error("Unable to cancel task", error);
    };

    const onCancelSuccess = (data: UserTaskSaveResponse) => {
      setIsSaving(false);
      if (data.savedSuccessfully) {
        onCancelled();
      } else {
        onCancelError(data.errorReason);
      }
    };

    let answerSetDetails: AnswerSetUserTaskDetails | null = null;
    if (usageScenario === "JOURNEY-COLLAB-DOC") {
      answerSetDetails = {
        answerSetGuidId: currentAnswerSetUniqueId,
        clientFormId: formId,
      };
    }

    // Post the response
    setIsSaving(true);
    tasksApi.cancelTaskWithForm(
      {
        userTaskId: userTaskId,
        formId: cancelFormInstance.form.formId,
        answers: cancelFormInstance.response.answers,
        responseId: cancelFormInstance.response.responseId || null,
        questionIdsToPullCommentsFrom: matchingTaskType?.commentQuestionIds || [],
        answerSetDetails: answerSetDetails,
        responseStatus: "SUBMITTED"
      },
      onCancelSuccess,
      onCancelError
    );
  };

  let headerContent: JSX.Element = (
    <WarningAlert
      message={t("TaskType.Popup.ExistingCancelResponseView.WarningMessage", {
        taskType: singularTaskTypeName,
      })}
      prefix={null}
    />
  );

  let footerContent: JSX.Element = (
    <div>
      <InfoAlert
        message={t("TaskType.Popup.ExistingCancelResponseView.InfoMessage", {
          taskType: singularTaskTypeName,
        })}
        prefix={null}
      />
      <div className="flex flex-row justify-end gap-4">
        <button onClick={goBackToBodyDetails} className="hover:underline">
          {t("TaskType.Popup.Buttons.GoBack")}
        </button>
        <button
          onClick={onEditResponse}
          className="btn-secondary"
          disabled={isSaving}
        >
          {t("TaskType.Popup.Buttons.Edit")}
        </button>
        <button
          onClick={onCancelWithForm}
          className="btn-primary"
          disabled={isSaving}
        >
          {t("TaskType.Popup.Buttons.Submit")}
        </button>
      </div>
    </div>
  );

  return (
    <ScrollableModalContentTemplate
      header={headerContent}
      footer={footerContent}
      bodyRef={scrollableContainerRef}
      containerClassName="flex flex-col gap-1 md:gap-0"
    >
      <div className="pt-2">
        <ExistingTaskResponseSummaryTable
          simpleFormInstance={cancelFormInstance}
          ownerEmployee={ownerEmployee}
        />
      </div>
    </ScrollableModalContentTemplate>
  );
};

export default AdvancedTaskExistingCancelResponseView;
